// src/realtime/JobsProvider.tsx
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import { JobStatus, JobUpdatePayload } from "../components/JobStatusPanel";
import { JobService } from "../services/JobService";
import * as signalR from "@microsoft/signalr";
import { getJobsHubConnection } from "../common/jobHubsClient";

type JobUpdateHandler = (payload: JobUpdatePayload) => void;

type JobsContextValue = {
    jobs: Record<string, JobUpdatePayload | null>;
    connected: boolean;
    watchJob: (jobId: string, options?: WatchJobOptions, cb?: JobUpdateHandler) => Promise<() => void>;
    watchJobs: (
        jobIds: string[],
        options?: WatchJobOptions,
        cb?: JobUpdateHandler
    ) => Promise<() => void>;
};

interface WatchJobOptions {
    onSubscribePushFirst?: boolean;
}

const JobsContext = createContext<JobsContextValue | undefined>(undefined);

export function JobsProvider({ children }: { children: React.ReactNode }) {
    const [connected, setConnected] = useState(false);
    const [jobs, setJobs] = useState<Record<string, JobUpdatePayload | null>>({});
    const handlersRegisteredRef = useRef(false);

    // jobId -> Set<callbacks>
    const callbacksRef = useRef<Map<string, Set<JobUpdateHandler>>>(new Map());

    // subscribed server-side
    const subscribedIdsRef = useRef<Set<string>>(new Set());

    const ensureSubscribedAndRefreshed = useCallback(
        async (jobIds: string[], options?: WatchJobOptions) => {
            const connection = getJobsHubConnection();

            // subscribe on server if connected
            if (
                connection.state === signalR.HubConnectionState.Connected &&
                jobIds.length > 0
            ) {
                try {
                    await connection.invoke("SubscribeBulk", jobIds);
                } catch (e) {
                }
            }

            // bulk refetch
            try {
                const res = await JobService.getJobsByIds(jobIds);
                if (!res.error && res.data) {
                    const bulkJobs = res.data.jobs;

                    setJobs((prev) => {
                        const nextState = { ...prev };

                        for (const job of bulkJobs) {
                            const jobId = job.id
                            const current = nextState[jobId];
                            const incoming = job;

                            const isStatusOld =
                                current &&
                                incoming &&
                                getStatusAsNumberStep(current.status) >
                                getStatusAsNumberStep(incoming.status);

                            const isOldUpdate =
                                (current?.updatedAt &&
                                    incoming?.updatedAt &&
                                    current.updatedAt > incoming.updatedAt) ||
                                isStatusOld;

                            if (isOldUpdate) continue;

                            nextState[jobId] = {
                                ...current,
                                ...incoming,
                                jobId,
                                autoDownload: current?.autoDownload,
                            };
                        }

                        return nextState;
                    });
                }
            } catch (err) {
            }
        },
        []
    );

    useEffect(() => {
        const connection = getJobsHubConnection();
        let disposed = false;

        // ✅ Register handlers only once per connection
        if (!handlersRegisteredRef.current) {
            connection.on("JobUpdated", (payload: JobUpdatePayload) => {
                const jobId = payload.jobId;

                // 1. update global state
                setJobs((prev) => {
                    const current = prev[jobId];
                    let next: JobUpdatePayload = { ...payload };

                    const isOldUpdate =
                        (current?.updatedAt &&
                            next.updatedAt &&
                            current.updatedAt > next.updatedAt) ||
                        (current &&
                            next &&
                            getStatusAsNumberStep(current.status) >
                            getStatusAsNumberStep(next.status));

                    // stale guard
                    if (isOldUpdate) {
                        return prev;
                    }

                    return { ...prev, [jobId]: next };
                });

                // 2. notify watchers
                const subs = callbacksRef.current.get(jobId);
                if (subs) {
                    subs.forEach((cb) => {
                        try {
                            cb(payload);
                        } catch (e) {
                        }
                    });
                }
            });

            handlersRegisteredRef.current = true;
        }

        const startConnection = async () => {
            // ✅ avoid double-start (StrictMode, remounts, etc.)
            if (
                connection.state === signalR.HubConnectionState.Connected ||
                connection.state === signalR.HubConnectionState.Connecting
            ) {
                if (!disposed) setConnected(true);
                return;
            }

            try {
                await connection.start();
                if (disposed) return;
                setConnected(true);
            } catch (e) {
                if (!disposed) {
                    setConnected(false);
                }
            }
        };

        startConnection();

        connection.onreconnected(async () => {
            if (disposed) return;
            setConnected(true);

            const ids = Array.from(subscribedIdsRef.current);
            if (ids.length) {
                await ensureSubscribedAndRefreshed(ids);
            }
        });


        connection.onclose(() => {
            if (!disposed) {
                setConnected(false);
            }
        });

        return () => {
            disposed = true;
        };
    }, [ensureSubscribedAndRefreshed]);

    const watchJobs = useCallback(
        async (jobIds: string[], options?: WatchJobOptions, cb?: JobUpdateHandler) => {
            if (cb) {
                jobIds.forEach((jobId) => {
                    let set = callbacksRef.current.get(jobId);
                    if (!set) {
                        set = new Set();
                        callbacksRef.current.set(jobId, set);
                    }
                    set.add(cb);
                });
            }

            // 2) track desired subscriptions & send SubscribeMany once
            const newIds: string[] = [];
            for (const jobId of jobIds) {
                if (!subscribedIdsRef.current.has(jobId)) {
                    subscribedIdsRef.current.add(jobId);
                    newIds.push(jobId);
                }
            }

            await ensureSubscribedAndRefreshed(newIds, options);

            // 5) cleanup – remove cb for all ids
            return () => {
                if (!cb) return;
                jobIds.forEach((jobId) => {
                    const set = callbacksRef.current.get(jobId);
                    if (!set) return;
                    set.delete(cb);
                    if (set.size === 0) callbacksRef.current.delete(jobId);
                });
            };
        },
        [ensureSubscribedAndRefreshed]
    );

    // keep your single-job version as a wrapper
    const watchJob = useCallback(
        (jobId: string, options?: WatchJobOptions, cb?: JobUpdateHandler) =>
            watchJobs([jobId], options, cb),
        [watchJobs]
    );

    return (
        <JobsContext.Provider value={{ jobs, connected, watchJob, watchJobs }}>
            {children}
        </JobsContext.Provider>
    );
}

export function useJobs() {
    const ctx = useContext(JobsContext);
    if (!ctx) throw new Error("useJobs must be used within JobsProvider");
    return ctx;
}

function getStatusAsNumberStep(status: JobStatus) {
    switch (status) {
        case "Queued": return 0;
        case "Running": return 1
        case "Completed": return 2
        case "Failed": return 3
    }
}
